'use client';

import { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import AdvancedDropdown from '@/components/AdvancedDropdown';
import { busAPI, routeAPI } from '@/services/api';
import { Suspense } from 'react';
import Footer from '@/components/Footer';
import { PageMetadataUpdater } from '@/hooks/usePageMetadata';

// Helper function to get local date string in YYYY-MM-DD format
const getLocalDateString = () => {
  const now = new Date();
  const utc = now.toISOString().split('T')[0];
  return utc;
};

// Helper function to get date 7 days from now in YYYY-MM-DD format
const getMaxDateString = () => {
  const now = new Date();
  now.setDate(now.getDate() + 7);
  const utc = now.toISOString().split('T')[0];
  return utc;
};

// Create a client component for the search content
function SearchContent() {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [searchData, setSearchData] = useState({
    from: '',
    to: '',
    date: ''
  });
  const [routes, setRoutes] = useState([]);

  const [buses, setBuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [origins, setOrigins] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [hasSearched, setHasSearched] = useState(false);

  // Initialize search data from URL parameters
  useEffect(() => {
    if (searchParams) {
      const from = searchParams.get('from') || '';
      const to = searchParams.get('to') || '';
      const date = searchParams.get('date') || '';

      setSearchData(prev => ({
        ...prev,
        from,
        to,
        date
      }));
    }
  }, [searchParams]);

  // Fetch routes on component mount
  useEffect(() => {
    const fetchRoutes = async () => {
      try {
        setOptionsLoading(true);
        const routeData = await routeAPI.getRoutes();

        // Extract unique origins and destinations
        const uniqueOrigins = [...new Set(routeData.map(route => route.origin))];
        const uniqueDestinations = [...new Set(routeData.map(route => route.destination))];

        setOrigins(uniqueOrigins);
        setDestinations(uniqueDestinations);
      } catch (err) {
        console.error('Failed to fetch routes:', err);
        // Check if it's an authentication error
        if (err.message && err.message.includes('Session expired')) {
          setError('Your session has expired. Please log in again.');
        }
      } finally {
        setOptionsLoading(false);
      }
    };

    fetchRoutes();
  }, []);

  // Auto-search when URL parameters are present and routes are loaded
  useEffect(() => {
    if (searchParams && !hasSearched && !optionsLoading) {
      const from = searchParams.get('from') || '';
      const to = searchParams.get('to') || '';
      const date = searchParams.get('date') || '';

      // If we have search parameters, trigger the search
      if (from && to && date) {
        // Update the search data state
        setSearchData(prev => ({
          ...prev,
          from,
          to,
          date
        }));

        // Trigger search after a small delay to ensure state is updated
        setTimeout(() => {
          handleSearch({ preventDefault: () => { } }, true);
        }, 100);
      }
    }
  }, [searchParams, hasSearched, optionsLoading]);

  useEffect(() => {
    const fetchRoutes = async () => {
      try {
        const routeData = await routeAPI.getRoutes();
        setRoutes(routeData);
      } catch (err) {
        console.error('Failed to fetch routes:', err);
        setError('Failed to load routes');
      }
    };
    fetchRoutes();
  }, []);

  const handleSearch = async (e, isAutoSearch = false) => {
    if (!isAutoSearch) {
      e.preventDefault();
    }
    // Validate that date is within today to 7 days from now
    const today = new Date();
    const maxDate = new Date();
    maxDate.setDate(today.getDate() + 7);
    const selectedDate = new Date(searchData.date);
    
    // Normalize dates to compare only date parts (without time)
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const selectedDateNormalized = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
    const maxDateNormalized = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
    
    if (selectedDateNormalized < todayDate || selectedDateNormalized > maxDateNormalized) {
      setError('Please select a date within the next 7 days');
      return;
    }
    // Only proceed if we have the required search parameters
    if (!searchData.from || !searchData.to || !searchData.date) {
      if (!isAutoSearch) {
        setError('Please fill in all required fields');
      }
      return;
    }

    setLoading(true);
    setError('');
    setHasSearched(true);

    try {
      // Call the API to search for buses with schedules
      const response = await busAPI.getBusesWithSchedules({
        origin: searchData.from,
        destination: searchData.to,
        date: searchData.date
      });

      const sortedBuses = response.sort((a, b) => {
        const timeA = a.departureTime.split(':').map(Number);
        const timeB = b.departureTime.split(':').map(Number);
        if (timeA[0] !== timeB[0]) {
          return timeA[0] - timeB[0];
        }
        return timeA[1] - timeB[1];
      });
      
      // Filter out duplicates based on bus id and departure time
      const uniqueBuses = sortedBuses.filter((bus, index, self) => 
        index === self.findIndex((b) => 
          b.id === bus.id && b.departureTime === bus.departureTime
        )
      );
      setBuses(uniqueBuses);
    } catch (err) {
      // Check if it's an authentication error
      if (err.message && err.message.includes('Session expired')) {
        setError('Your session has expired. Please log in again.');
        // Add a small delay before redirecting to show the message
        setTimeout(() => {
          router.push('/login');
        }, 2000);
      } else {
        setError(err.message || 'Failed to search for buses. Please try again.');
      }
      setBuses([]);
    } finally {
      setLoading(false);
    }
  };

  // Function to update seat availability for a specific bus on a different date
  const updateBusAvailability = async (busId, date) => {
    try {
      const busDetails = await busAPI.getBusById(busId, { date });
      return busDetails;
    } catch (err) {
      console.error('Failed to update bus availability:', err);
      return null;
    }
  };

  // Function to convert 24-hour time to 12-hour AM/PM format
  const formatTimeToAmPm = (timeString) => {
    if (!timeString) return '';
    
    // Handle different time formats (HH:MM or HH:MM:SS)
    let [hours, minutes] = timeString.split(':');
    
    // Convert to integers
    hours = parseInt(hours, 10);
    minutes = parseInt(minutes, 10) || 0;
    
    // Determine AM or PM
    const period = hours >= 12 ? 'PM' : 'AM';
    
    // Convert to 12-hour format
    if (hours === 0) {
      hours = 12;
    } else if (hours > 12) {
      hours -= 12;
    }
    
    // Format with leading zero for minutes but not for hours in 12-hour format
    const formattedMinutes = minutes.toString().padStart(2, '0');
    
    return `${hours}:${formattedMinutes} ${period}`;
  };

  // Function to handle date change for a specific bus
  const handleDateChange = async (busIndex, newDate) => {
    if (!newDate) return;
    
    const updatedBuses = [...buses];
    const bus = updatedBuses[busIndex];
    
    // Show loading state for this specific bus
    updatedBuses[busIndex] = { ...bus, loadingAvailability: true };
    setBuses(updatedBuses);
    
    try {
      const updatedBus = await updateBusAvailability(bus.id, newDate);
      if (updatedBus) {
        updatedBuses[busIndex] = {
          ...updatedBus,
          loadingAvailability: false
        };
        setBuses(updatedBuses);
      }
    } catch (err) {
      console.error('Error updating bus availability:', err);
      // Revert to previous state if there was an error
      updatedBuses[busIndex] = { ...bus, loadingAvailability: false };
      setBuses(updatedBuses);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Skip passengers field as it's been removed
    if (name === 'passengers') return;
    setSearchData({
      ...searchData,
      [name]: value
    });
  };

  return (<>
    <div className="max-w-4xl mx-auto py-8">
      <PageMetadataUpdater pageKey="search" />
      <h1 className="text-3xl font-bold text-center mb-8">Search Buses</h1>

      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <form onSubmit={handleSearch}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <AdvancedDropdown
                options={optionsLoading ? [] : origins}
                value={searchData.from}
                onChange={handleChange}
                placeholder={optionsLoading ? "Loading..." : "Enter departure city"}
                label="From"
                id="from"
                name="from"
                required
                allowCustomInput={true}
              />
            </div>

            <div>
              <AdvancedDropdown
                options={optionsLoading ? [] : destinations}
                value={searchData.to}
                onChange={handleChange}
                placeholder={optionsLoading ? "Loading..." : "Enter destination city"}
                label="To"
                id="to"
                name="to"
                required
                allowCustomInput={true}
              />
            </div>

            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                id="date"
                name="date"
                value={searchData.date}
                onChange={handleChange}
                min={getLocalDateString()}
                max={getMaxDateString()}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#007f80]"
                required
              />
            </div>


          </div>
          {routes.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-medium text-gray-700 mb-2">Popular Routes</h3>
              <div className="flex overflow-x-auto gap-2 pb-2 scrollbar-hide">
                {routes.slice(0, 10).map((route, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => {
                      setSearchData(prev => ({
                        ...prev,
                        from: route.origin,
                        to: route.destination
                      }));
                    }}
                    className="bg-gray-100 hover:bg-[#16A34A] hover:text-white text-gray-800 px-3 py-1 rounded-md text-sm transition-colors whitespace-nowrap flex-shrink-0"
                  >
                    {route.origin} → {route.destination}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 text-center">
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 bg-[#007f80] text-white font-medium rounded-md hover:bg-[#006667] focus:outline-none focus:ring-2 focus:ring-[#007f80] focus:ring-offset-2 disabled:opacity-50"
            >
              {loading ? 'Searching...' : 'Search Buses'}
            </button>
          </div>
        </form>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}
      <div>
        <h2 className="text-2xl font-bold mb-4">Available Buses</h2>

        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#007f80]"></div>
            <p className="mt-2">Searching for available buses...</p>
          </div>
        ) : buses.length > 0 ? (
          <div className="grid grid-cols-1 gap-4">
            {buses.map(bus => (
              <div key={bus.id} className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 sm:p-6 hover:shadow-md transition-shadow touch-manipulation">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  {bus.imageUrl ? (
                    <div className="flex-shrink-0 w-full sm:w-48 h-32 sm:h-auto mb-4 sm:mb-0 sm:mr-4">
                      <img
                        src={`${process.env.NEXT_PUBLIC_IMAGE_URL}${bus.imageUrl}`}
                        alt={`Image of ${bus.busNumber}`}
                        className="w-full h-full object-cover rounded-md"
                      />
                    </div>
                  ) : (
                    <div className="flex-shrink-0 w-full sm:w-48 h-32 sm:h-auto mb-4 sm:mb-0 sm:mr-4 flex items-center justify-center bg-gray-200 rounded-md">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" className="bi bi-bus-front text-gray-400 h-16 w-16 mx-auto my-auto" viewBox="0 0 16 16">
                        <path d="M5 11a1 1 0 1 1-2 0 1 1 0 0 1 2 0m8 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0m-6-1a1 1 0 1 0 0 2h2a1 1 0 1 0 0-2zm1-6c-1.876 0-3.426.109-4.552.226A.5.5 0 0 0 3 4.723v3.554a.5.5 0 0 0 .448.497C4.574 8.891 6.124 9 8 9s3.426-.109 4.552-.226A.5.5 0 0 0 13 8.277V4.723a.5.5 0 0 0-.448-.497A44 44 0 0 0 8 4m0-1c-1.837 0-3.353.107-4.448.22a.5.5 0 1 1-.104-.994A44 44 0 0 1 8 2c1.876 0 3.426.109 4.552.226a.5.5 0 1 1-.104.994A43 43 0 0 0 8 3"/>
                        <path d="M15 8a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1V2.64c0-1.188-.845-2.232-2.064-2.372A44 44 0 0 0 8 0C5.9 0 4.208.136 3.064.268 1.845.408 1 1.452 1 2.64V4a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1v3.5c0 .818.393 1.544 1 2v2a.5.5 0 0 0 .5.5h2a.5.5 0 0 0 .5-.5V14h6v1.5a.5.5 0 0 0 .5.5h2a.5.5 0 0 0 .5-.5v-2c.607-.456 1-1.182 1-2zM8 1c2.056 0 3.71.134 4.822.261.676.078 1.178.66 1.178 1.379v8.86a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 11.5V2.64c0-.72.502-1.301 1.178-1.379A43 43 0 0 1 8 1"/>
                      </svg>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">{bus.busNumber}</h3>
                      <div className="flex items-center text-amber-500">
                        <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                        </svg>
                        <span className="text-sm font-medium text-gray-700 ml-1">{bus.averageRating || '0.0'}</span>
                        {bus.totalReviews > 0 && (
                          <span className="text-xs text-gray-500">({bus.totalReviews})</span>
                        )}
                      </div>
                    </div>
                    <div className="mt-3">
                      <div className="grid grid-cols-2 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        <div>
                          <span className="text-xs sm:text-sm text-gray-500">Route</span>
                          <p className="font-medium text-sm sm:text-base">{bus.Route?.origin} → {bus.Route?.destination}</p>
                        </div>
                        <div>
                          <span className="text-xs sm:text-sm text-gray-500">Departure</span>
                          <p className="font-medium text-sm sm:text-base">{formatTimeToAmPm(bus.departureTime)}</p>
                        </div>
                        <div>
                          <span className="text-xs sm:text-sm text-gray-500">Arrival</span>
                          <p className="font-medium text-sm sm:text-base">{formatTimeToAmPm(bus.arrivalTime)}</p>
                        </div>
                        <div>
                          <span className="text-xs sm:text-sm text-gray-500">Type</span>
                          <p className="font-medium text-sm sm:text-base">{bus.busType}</p>
                        </div>
                        <div>
                          <span className="text-xs sm:text-sm text-gray-500">Available Seats</span>
                          <p className="font-medium text-sm">
                            {bus.loadingAvailability ? (
                              <span className="flex items-center">
                                <span className="h-4 w-4 animate-spin rounded-full border-t border-b border-[#007f80] mr-2"></span>
                                Loading...
                              </span>
                            ) : (
                              <span>{bus.availableSeats} of {bus.totalSeats} seats</span>
                            )}
                          </p>
                        </div>
                        {/* {bus.pickupPoints && bus.pickupPoints.length > 0 && (
                          <div>
                            <span className="text-xs sm:text-sm text-gray-500">Pickup Points</span>
                            <p className="font-medium text-sm sm:text-base">
                              {bus.pickupPoints.slice(0, 3).map(p => p.name).join(', ')}
                              {bus.pickupPoints.length > 3 && ` +${bus.pickupPoints.length - 3} more`}
                            </p>
                          </div>
                        )} */}
                        {/* {bus.dropoffPoints && bus.dropoffPoints.length > 0 && (
                          <div>
                            <span className="text-xs sm:text-sm text-gray-500">Drop-off Points</span>
                            <p className="font-medium text-sm sm:text-base">
                              {bus.dropoffPoints.slice(0, 3).map(p => p.name).join(', ')}
                              {bus.dropoffPoints.length > 3 && ` +${bus.dropoffPoints.length - 3} more`}
                            </p>
                          </div>
                        )} */}
                      </div>
                    </div>
                  </div>

                  <div className="sm:text-right flex-shrink-0">
                    <p className="text-xl sm:text-2xl font-bold text-[#007f80]">৳{parseFloat(bus.fare).toFixed(2)}</p>
                    <p className="text-xs sm:text-sm text-gray-500">per seat</p>
                    <a
                      href={`/booking/${bus.id}/select-seats?date=${searchData.date}&scheduleId=${bus.scheduleId || ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        const dateInput = document.getElementById(`date-${bus.id}`);
                        let selectedDate = searchData.date;
                        if (dateInput) {
                          selectedDate = dateInput.value || searchData.date;
                        }
                        router.push(`/booking/${bus.id}/select-seats?date=${selectedDate}&scheduleId=${bus.scheduleId || ''}`);
                      }}
                      className="mt-3 sm:mt-2 inline-block w-full sm:w-auto px-4 py-3 sm:px-4 sm:py-2 bg-[#007f80] text-white text-sm font-medium rounded-md hover:bg-[#006667] transition duration-300 text-center sm:text-left touch-manipulation"
                    >
                      Select Seats
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : !loading && hasSearched ? (
          <div className="text-center py-8 bg-white rounded-lg shadow-sm">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No buses found</h3>
            <p className="mt-1 text-sm text-gray-500">Try adjusting your search criteria.</p>
          </div>
        ) : null}
      </div>
    </div>
    {/* Footer Section */}
    <Footer />
  </>
  );
}

// Main page component with Suspense
export default function SearchPage() {
  return (
    <Suspense fallback={
      <div className="max-w-4xl mx-auto py-8 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#007f80]"></div>
        <p className="mt-2">Loading search page...</p>
      </div>
    }>
      <SearchContent />
    </Suspense>
  );
}
